import React from 'react';
import { Instagram, Facebook, Youtube, Heart, Mail, Phone } from 'lucide-react';
import { socialLinks, contactInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const LOGO_URL = "/pizzatoons-logo.png";

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const quickLinks = [
    { name: t.nav.home, href: '#home' },
    { name: t.nav.story, href: '#story' },
    { name: t.nav.franchise, href: '#franchise' },
    { name: t.nav.faq, href: '#faq' },
    { name: t.nav.outlets, href: '#outlets' },
    { name: t.nav.contact, href: '#contact' }
  ];

  const legalLinks = [
    { name: language === 'en' ? 'Privacy Policy' : 'गोपनीयता धोरण', href: '/privacy-policy' },
    { name: language === 'en' ? 'Terms & Conditions' : 'नियम आणि अटी', href: '/terms-conditions' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-6">
              <img
                src={LOGO_URL}
                alt="PizzaToons"
                className="h-14 w-auto object-contain bg-white/90 p-2 rounded-lg"
              />
            </div>
            <p className="text-gray-400 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {language === 'en' ? "India's growing pizza franchise built on quality, trust, and transparency. Join our family today." : "गुणवत्ता, विश्वास आणि पारदर्शकतेवर आधारित भारतातील वेगाने वाढणारी पिझ्झा फ्रँचायझी. आजच आमच्या परिवारात सामील व्हा."}
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-red-500 flex items-center justify-center transition-colors duration-300">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-red-500 flex items-center justify-center transition-colors duration-300">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-red-500 flex items-center justify-center transition-colors duration-300">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {language === 'en' ? 'Quick Links' : 'द्रुत दुवे'}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-gray-400 hover:text-white transition-colors duration-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-lg font-bold mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {language === 'en' ? 'Legal' : 'कायदेशीर'}
            </h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-gray-400 hover:text-white transition-colors duration-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6 inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>
              <span className="w-2 h-2 rounded-full bg-green-400"></span>
              {language === 'en' ? 'FSSAI Certified' : 'FSSAI प्रमाणित'}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {language === 'en' ? 'Contact' : 'संपर्क'}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-red-400 mt-1 flex-shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="text-gray-400 hover:text-white transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-red-400 mt-1 flex-shrink-0" />
                <a href={`tel:${contactInfo.phone}`} className="text-gray-400 hover:text-white transition-colors" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {contactInfo.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm text-center" style={{ fontFamily: "'Poppins', sans-serif" }}>
              © {new Date().getFullYear()} PizzaToons. {language === 'en' ? 'All rights reserved.' : 'सर्व हक्क राखीव.'}
            </p>
            <p className="text-gray-400 text-sm flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {language === 'en' ? 'Designed & developed by' : 'डिझाईन आणि विकसित केले'} <a href="https://nirosha.org" target="_blank" rel="noopener noreferrer" className="text-white hover:text-red-500 transition-colors">Team Nirosha</a>.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
